import {
  Config,
  VideoResponse,
  PaginatedVideosResponse,
  ZypePlayerData,
} from "../types";

export class MediaService {
  private pwsHost: string;
  private showSlug: string;
  private baseUrl: string;
  private token: string | null = null;

  constructor(pwsHost: string, showSlug: string) {
    this.pwsHost = pwsHost;
    this.showSlug = showSlug;
    // Use the original URL pattern: //host/media/show
    this.baseUrl = `//${pwsHost}/media/${showSlug}`;
  }

  private async doMediaRequest(endpoint: string): Promise<any> {
    const url = `${this.baseUrl}/${endpoint}`;

    try {
      console.log(`🌐 Making direct API request to: https:${url}`);

      const response = await fetch(`https:${url}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          // Add token if available (like original)
          ...(this.token && { Authorization: `Bearer ${this.token}` }),
        },
        mode: "cors",
        credentials: "omit",
      });

      console.log(
        `📡 Response status: ${response.status} ${response.statusText}`
      );

      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      console.log(`✅ API response received:`, data);
      return data;
    } catch (error) {
      console.error("❌ Direct API request failed:", error);

      // Enhanced error reporting for CORS issues
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          `Network error - CORS issue or server unreachable. API: https:${url}`
        );
      }

      throw error;
    }
  }

  // Original API methods matching the old plugin structure
  async getConfig(
    includeVideos: boolean = true,
    perPage: number = 12
  ): Promise<Config> {
    try {
      const endpoint = `config?includeVideos=${includeVideos}&perPage=${perPage}`;
      return await this.doMediaRequest(endpoint);
    } catch (error) {
      console.error("🔧 Config request failed, providing fallback:", error);

      // Provide a fallback config for development/testing
      if (process.env.NODE_ENV === "development") {
        console.warn("🚧 Using fallback config for development");
        return {
          tabs: [
            {
              slug: "live",
              title: "Live",
              hasLive: 1,
              videos: [],
            },
            {
              slug: "on-demand",
              title: "On Demand",
              hasLive: 0,
              videos: [],
            },
          ],
        };
      }

      throw error;
    }
  }

  async getVideoBySlug(slug: string): Promise<VideoResponse> {
    const endpoint = slug === "live" ? "on-air" : `videos/slug/${slug}`;
    return this.doMediaRequest(endpoint);
  }

  async getMediaBySlug(slug: string): Promise<VideoResponse> {
    return this.getVideoBySlug(slug);
  }

  async getVideoById(id: string): Promise<VideoResponse> {
    const endpoint = id === "live" ? "on-air" : `videos/${id}`;
    return this.doMediaRequest(endpoint);
  }

  async getMediaByLegacyId(id: number): Promise<VideoResponse> {
    return this.doMediaRequest(`videos/source-id/${id}`);
  }

  async getOnAirVideo(manifest: boolean = false): Promise<VideoResponse> {
    const endpoint = `on-air${manifest ? "?manifest=true" : ""}`;
    return this.doMediaRequest(endpoint);
  }

  async getOnAirStatus(): Promise<any> {
    return this.doMediaRequest("on-air-status");
  }

  // Updated to match original API structure with resume-based pagination
  async getVideos(
    resumeToken?: string,
    options?: Record<string, any>
  ): Promise<PaginatedVideosResponse> {
    try {
      let endpoint = "videos";

      if (resumeToken) {
        endpoint += `?resume=${resumeToken}`;
      } else if (options && Object.keys(options).length > 0) {
        const params = Object.entries(options).reduce((acc, [key, value]) => {
          return acc + `&${key}=${value}`;
        }, "");
        if (params) {
          endpoint += `?${params.slice(1)}`; // Remove leading &
        }
      }

      return await this.doMediaRequest(endpoint);
    } catch (error) {
      console.error("🔧 Videos request failed:", error);

      // Provide fallback empty response
      if (process.env.NODE_ENV === "development") {
        console.warn("🚧 Using fallback empty videos response");
        return {
          videos: [],
          pagination: {
            resume: undefined,
            hasMore: false,
            total: 0,
          },
        };
      }

      throw error;
    }
  }

  // For tab-specific videos - using the original pattern
  async getTabVideos(
    tabSlug?: string,
    resumeToken?: string
  ): Promise<PaginatedVideosResponse> {
    if (!tabSlug) {
      return this.getVideos(resumeToken);
    }

    // In the original, tab filtering might be done client-side or with different params
    return this.getVideos(resumeToken, { tab: tabSlug });
  }

  // Zype player data method matching original is-audio endpoint
  async getZypePlayerData(
    videoId: string,
    onAir: boolean = false
  ): Promise<ZypePlayerData> {
    try {
      const endpoint = `is-audio/${videoId}?onAir=${onAir}`;
      return await this.doMediaRequest(endpoint);
    } catch (error) {
      console.error("🔧 Zype player data request failed:", error);

      // Provide fallback player data for development
      if (process.env.NODE_ENV === "development") {
        console.warn("🚧 Using fallback Zype player data");
        return {
          authenticated: false,
          playerApiKey: "development-fallback-key",
          isAudio: false,
        };
      }

      throw error;
    }
  }

  async getIsAudio(
    videoId: string,
    onAir: boolean = false
  ): Promise<ZypePlayerData> {
    return this.getZypePlayerData(videoId, onAir);
  }
}
